/**
 * Lo que te falta para reservar, dicho donde está el deseo.
 *
 * POR QUÉ EXISTE. De 940 personas que abrieron la ficha de un carro, 24 podían
 * reservar. Y 885 —el 94%— **nunca abrieron la pantalla de verificación**. No es
 * que la intentaran y fallaran: nunca la empezaron. El KYC funciona: el DUI se
 * aprueba en 2,5 segundos con un 93% de aceptación.
 *
 * La causa es dónde se pregunta. `useBookingEligibility` se consultaba en UN
 * solo lugar de toda la app —`checkout.tsx`, la última pantalla— así que la
 * persona descubría los cinco requisitos después de elegir carro, fechas y
 * puntos de entrega, con la tarjeta en la mano. El esfuerzo más grande se
 * verificaba al final.
 *
 * TRES DECISIONES DE DISEÑO, Y NINGUNA ES COSMÉTICA:
 *
 * 1. SE MUESTRA LO HECHO, NO SOLO LO QUE FALTA. Una lista de cinco carencias se
 *    lee como un rechazo; la misma lista con dos palomitas se lee como un
 *    trámite a medio hacer. Es la diferencia entre "no calificás" y "te falta
 *    poco", y no cuesta nada decirlo de la segunda forma cuando es igual de
 *    cierto.
 *
 * 2. CADA PASO DICE CUÁNTO TARDA. Los minutos son reales, no optimistas: salen
 *    de lo que el proveedor tarda de verdad. La gente no abandona por el
 *    esfuerzo sino por no saber cuánto es. Un número acotado vence a un
 *    horizonte abierto.
 *
 * 3. UN SOLO BOTÓN, Y DICE LA VERDAD. Un botón que dice "Reservar" y lleva a
 *    verificación es una mentira más barata de escribir y más cara de cobrar.
 *    Cuando falta algo, el botón dice lo que va a pasar.
 *
 * FALLA ABIERTA. Mientras `resolved` sea falso no se pinta nada y el botón de
 * reservar queda como estaba: el servidor decide. Decirle "te falta el DUI" a
 * alguien que ya lo tiene, porque se cayó una consulta, es peor que dejarlo
 * tocar un botón que el servidor va a responder de todos modos.
 */
import { View, Text } from 'react-native'
import { useTranslation } from 'react-i18next'
import { usePathname, useRouter } from 'expo-router'
import { BadgeCheck, ChevronRight, Circle, Clock } from '@/components/ui/icons'
import { Pressable } from '@/components/ui/Pressable'
import { useSemanticColors, SPACING, RADIUS, INTERACTION } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import { FONTS } from '@/lib/brand'
import { BOOKING_BLOCKERS, type BookingBlocker } from '@/lib/types/verification.types'
import { completedSteps, orderedPendingSteps, terminalBlockerIn } from '@/lib/verification/steps'

export interface ReadyToBookProps {
  missing: BookingBlocker[]
  /** Solo se pinta cuando el servidor ya respondió. Ver "falla abierta". */
  resolved: boolean
  compact?: boolean
}

export function ReadyToBook({ missing, resolved, compact = false }: ReadyToBookProps) {
  const { t } = useTranslation()
  const router = useRouter()
  const pathname = usePathname()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  if (!resolved || missing.length === 0) return null

  /*
   * Un estado terminal GANA sobre cualquier lista de pasos.
   *
   * Antes esto solo miraba si TODO lo que faltaba era un estado, asi que a
   * alguien que pidio borrar su cuenta Y ademas no tenia telefono se le pintaba
   * "confirma tu telefono" -- un tramite que no le iba a desbloquear nada,
   * mientras la razon real se callaba. `identity_review_pending` no es
   * terminal y por eso convive con la lista: ahi los pasos si sirven.
   */
  const terminal = terminalBlockerIn(missing)

  if (terminal) {
    return (
      <View
        testID="vehicle-detail.ready-to-book"
        style={{
          backgroundColor: colors.surfaceSubtle,
          borderRadius: RADIUS.lg,
          padding: SPACING.md,
          gap: SPACING.xs,
        }}
      >
        <Text style={{ fontSize: 15, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}>
          {t(`verification.blocker_${terminal}_title`)}
        </Text>
        <Text style={{ fontSize: 13, fontFamily: FONTS.body, color: colors.textSecondary }}>
          {t(`verification.blocker_${terminal}_desc`)}
        </Text>
      </View>
    )
  }

  const pending = orderedPendingSteps(missing)
  const done = completedSteps(missing)
  // Los que este build no conoce se cuentan igual: callarlos haría que la suma
  // no cierre y que el botón prometa algo que el servidor va a negar.
  const unknown = missing.filter((m) => !BOOKING_BLOCKERS.includes(m))

  return (
    <View
      testID="vehicle-detail.ready-to-book"
      accessibilityRole="summary"
      style={{
        backgroundColor: colors.surfaceSubtle,
        borderRadius: RADIUS.lg,
        padding: SPACING.md,
        gap: SPACING.sm,
      }}
    >
      <Text style={{ fontSize: 15, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}>
        {t('verification.almost_there', { count: pending.length })}
      </Text>

      <View style={{ gap: SPACING.xs }}>
        {done.map((step) => (
          <View
            key={step.blocker}
            style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}
          >
            <BadgeCheck size={15} color={colors.success} strokeWidth={2.2} />
            <Text
              style={{ flex: 1, fontSize: 13, fontFamily: FONTS.body, color: colors.textMuted }}
              numberOfLines={1}
            >
              {t(step.labelKey)}
            </Text>
          </View>
        ))}

        {/*
          CADA PASO PENDIENTE SE TOCA Y TE LLEVA A HACERLO.

          Antes esto era una lista de solo lectura, y la unica salida era el
          boton de abajo, que lleva al hub de verificacion: una pantalla mas que
          recorrer para volver a elegir lo que ya estabas mirando. Leer "te falta
          confirmar tu telefono", quererlo hacer, y tener que buscarlo en otro
          lado es exactamente el paso de mas que hace que la gente no empiece.
          Medido: de 1.088 sin telefono confirmado, 1.057 nunca escribieron un
          numero.

          Los ya hechos NO se tocan a proposito. Un renglon con un cheque verde
          que ademas es un boton invita a deshacer algo que costo trabajo.
        */}
        {pending.map((step) => (
          <Pressable
            key={step.blocker}
            onPress={() =>
              // El origen viaja con el paso: asi la flecha de atras de la
              // pantalla de verificacion devuelve a ESTE carro y no al tab
              // Perfil. Sin esto la persona pierde el carro que queria y tiene
              // que buscarlo de nuevo -- ver el comentario de `safeInternalPath`
              // en components/ui/ScreenHeader.tsx.
              router.push(`${step.href}?from=${encodeURIComponent(pathname)}` as never)
            }
            accessibilityRole="button"
            accessibilityLabel={t('verification.go_do_step', { step: t(step.labelKey) })}
            testID={`ready-to-book.step-${step.blocker}`}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: SPACING.sm,
              // El renglon mide 15pt; sin esto el area tactil seria la mitad del
              // minimo. El margen negativo se lo devuelve al ritmo visual.
              minHeight: INTERACTION.minTouchTarget,
              marginVertical: -SPACING.xs,
            }}
          >
            <Circle size={15} color={colors.textMuted} strokeWidth={2} />
            <Text
              style={{ flex: 1, fontSize: 13, fontFamily: FONTS.body, color: colors.textPrimary }}
              numberOfLines={1}
            >
              {t(step.labelKey)}
            </Text>
            {!compact && (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3 }}>
                <Clock size={12} color={colors.textMuted} strokeWidth={2} />
                <Text style={{ fontSize: 12, fontFamily: FONTS.body, color: colors.textMuted }}>
                  {t(step.etaKey)}
                </Text>
              </View>
            )}
            {/* Un chevron promete que hay algo del otro lado. Ahora lo hay. */}
            <ChevronRight size={14} color={colors.textMuted} strokeWidth={2} />
          </Pressable>
        ))}

        {unknown.length > 0 && (
          <Text style={{ fontSize: 13, fontFamily: FONTS.body, color: colors.textPrimary }}>
            {t('steps.unknown', { count: unknown.length })}
          </Text>
        )}
      </View>

      <Text style={{ fontSize: 12, fontFamily: FONTS.body, color: colors.textSecondary }}>
        {t('verification.one_time_only')}
      </Text>
    </View>
  )
}
